import {t, Trans} from '@lingui/macro';
import React, {ReactNode} from 'react';

import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {newPalette} from '../../../../libs/shared/ui/src/styles/colorStyles';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import assignToCustomField from '../../../assets/images/ruleLibrary/assignToCustomField.png';
import customerSatisfactionImage from '../../../assets/images/ruleLibrary/customerSatisfaction.png';
import customRuleImage from '../../../assets/images/ruleLibrary/customRule.png';
import requiredTaggingImage from '../../../assets/images/ruleLibrary/requiredTagging.png';
import slaImage from '../../../assets/images/ruleLibrary/sla.png';
import {CompanyFeaturesEnum} from '../../../models/api/companyModel';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {RuleComplexRecipeTypesEnum} from '../ruleRecipeModel';
import {RuleRecipe, RuleRecipeCategoriesEnum} from './recipes';
import {DisplayableRuleRecipe} from './ruleLibraryRecipeCard';

/*
 * Constants.
 */

export const RecipeCategoryStyles: Partial<
  Record<RuleRecipeCategoriesEnum, readonly [IconName, PaletteColorsEnum]>
> = {
  [RuleRecipeCategoriesEnum.ASSIGN]: ['assignFilled', PaletteColorsEnum.BLUE],
  [RuleRecipeCategoriesEnum.MOVE]: ['moveRightFilled', PaletteColorsEnum.PURPLE],
  [RuleRecipeCategoriesEnum.NOTIFY]: ['bell', PaletteColorsEnum.YELLOW],
  [RuleRecipeCategoriesEnum.ARCHIVE]: ['archiveFilled', PaletteColorsEnum.GREEN],
  [RuleRecipeCategoriesEnum.REPLY]: ['sendFilled', PaletteColorsEnum.TEAL],
  [RuleRecipeCategoriesEnum.SLA]: ['slaFilled', PaletteColorsEnum.ORANGE],
  [RuleRecipeCategoriesEnum.TAG]: ['tagFilled', PaletteColorsEnum.RED],
  [RuleRecipeCategoriesEnum.COMMENT]: ['chatFilled', PaletteColorsEnum.PINK],
  // The only displayed smart rule recipe is a notify recipe
  [RuleRecipeCategoriesEnum.SMART_RULES]: ['bell', PaletteColorsEnum.YELLOW],
  [RuleRecipeCategoriesEnum.ACCOUNT]: ['assignFilled', PaletteColorsEnum.BLUE],
};

interface Section {
  id: string;
  title: ReactNode;
  content: (
    recipes: ReadonlyArray<RuleRecipe>,
    localize: Localizer,
  ) => ReadonlyArray<Omit<DisplayableRuleRecipe, 'url'>>;
  imageUrl?: string;
  backgroundColor?: string;
  learnMore?: {
    text: ReactNode;
    url: string;
  };
}

export const ruleLibrarySections: ReadonlyArray<Section> = [
  {
    id: 'custom_rule',
    title: (
      <Trans id="rule-library-category-custom-title">Create advanced workflows tailored to your needs</Trans>
    ),
    content: (_, localize) => [
      {
        id: 'custom_rule',
        categories: [],
        type: 'custom_rule',
        title: localize(t('rule-library-custom-rule-title')`Custom rule`),
        description: localize(
          t(
            'rule-library-custom-rule-description',
          )`Use the full power of our rule engine to create a rule from scratch`,
        ),
        icon: 'gearFilled',
        imageUrl: customRuleImage,
        iconPalette: PaletteColorsEnum.GREY,
        backgroundColor: newPalette.yellow.highlight,
        requiredFeatures: [],
      },
    ],
  },
  {
    id: 'account',
    title: <Trans id="rule-library-category-account-title">Discover the power of Account-based rules</Trans>,
    content: (recipes, localize) =>
      getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.ACCOUNT),
    imageUrl: assignToCustomField,
    backgroundColor: newPalette.orange.highlight,
    learnMore: {
      text: <Trans id="rule-library-category-account-learn-more">Learn more about accounts</Trans>,
      url: 'https://help.front.com/t/p8h1dja/customer-beta-use-accounts-in-rules-to-automate-conversations',
    },
  },
  {
    id: 'smart_rules',
    title: <Trans id="rule-library-category-smart-rules-title">Discover the power of Smart Rules</Trans>,
    content: (recipes, localize) =>
      getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.SMART_RULES),
  },
  {
    id: 'comment',
    title: <Trans id="rule-library-category-comment-title">Enrich conversations with comments</Trans>,
    content: (recipes, localize) =>
      getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.COMMENT),
  },
  {
    id: 'sla',
    title: <Trans id="rule-library-category-sla-title">Ensure your team replies on time</Trans>,
    content: (_, localize) => [
      {
        id: RuleComplexRecipeTypesEnum.SLA,
        categories: [RuleRecipeCategoriesEnum.SLA],
        type: 'SLA',
        title: localize(t('rule-library-sla-title')`Service Level Agreement rule`),
        description: localize(
          t('rule-library-sla-description')`Tag unreplied messages after a specific time.`,
        ),
        icon: 'slaFilled',
        imageUrl: slaImage,
        iconPalette: PaletteColorsEnum.ORANGE,
        backgroundColor: newPalette.blue.tint,
        requiredFeatures: [CompanyFeaturesEnum.ADVANCED_RULES, CompanyFeaturesEnum.SLA_RULES],
      },
    ],
  },
  {
    id: 'required-tagging',
    title: <Trans id="rule-library-category-required-tagging-title">Classify every conversation</Trans>,
    content: (recipes, localize) => [
      {
        id: RuleComplexRecipeTypesEnum.REQUIRED_TAGGING,
        categories: [RuleRecipeCategoriesEnum.TAG],
        type: 'required_tagging',
        title: localize(t('rule-library-required-tagging-title')`Require tag`),
        description: localize(
          t(
            'rule-library-required-tagging-description',
          )`Enforce tagging before a conversation can be moved or archived.`,
        ),
        learnMoreUrl: 'https://help.front.com/t/60hgg0p/required-tagging',
        icon: 'tagFilled',
        imageUrl: requiredTaggingImage,
        iconPalette: PaletteColorsEnum.RED,
        backgroundColor: newPalette.green.tint,
        requiredFeatures: [CompanyFeaturesEnum.ADVANCED_RULES, CompanyFeaturesEnum.REQUIRED_TAGGING_RULES],
      },
      ...getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.TAG),
    ],
  },
  {
    id: 'notify',
    title: <Trans id="rule-library-category-notify-title">Give your team appropriate visibility</Trans>,
    content: (recipes, localize) => getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.NOTIFY),
  },
  {
    id: 'assign',
    title: <Trans id="rule-library-category-assign-title">Route messages to the right person</Trans>,
    content: (recipes, localize) => getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.ASSIGN),
  },
  {
    id: 'move',
    title: <Trans id="rule-library-category-move-title">Route messages to the right team</Trans>,
    content: (recipes, localize) => getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.MOVE),
  },
  {
    id: 'archive',
    title: <Trans id="rule-library-category-archive-title">Keep your inboxes tidy</Trans>,
    content: (recipes, localize) =>
      getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.ARCHIVE),
  },
  {
    id: 'csat',
    title: <Trans id="rule-library-category-csat-title">Increase your team’s efficiency</Trans>,
    content: (recipes, localize) => [
      {
        id: RuleComplexRecipeTypesEnum.CSAT,
        categories: [RuleRecipeCategoriesEnum.REPLY],
        type: 'CSAT',
        title: localize(t('rule-library-csat-title')`Customer Satisfaction Survey`),
        description: localize(
          t('rule-library-csat-description')`Collect satisfaction scores directly from Front.`,
        ),
        icon: 'sendFilled',
        imageUrl: customerSatisfactionImage,
        iconPalette: PaletteColorsEnum.TEAL,
        backgroundColor: newPalette.blue.tint,
        requiredFeatures: [CompanyFeaturesEnum.ADVANCED_RULES, CompanyFeaturesEnum.SURVEYS],
      },
      ...getRecipesDisplayData(recipes, localize, RuleRecipeCategoriesEnum.REPLY),
    ],
  },
];

/*
 * Helpers.
 */

function getRecipeIconProps(recipe: RuleRecipe): {iconPalette: PaletteColorsEnum; icon: IconName} {
  const category = recipe.categories[0];
  const [icon, iconPalette] = RecipeCategoryStyles[category] ?? ['teammateFilled', PaletteColorsEnum.BLUE];
  return {
    iconPalette,
    icon,
  };
}

function getRecipesDisplayData(
  recipes: ReadonlyArray<RuleRecipe>,
  localize: Localizer,
  category: RuleRecipeCategoriesEnum,
): ReadonlyArray<Omit<DisplayableRuleRecipe, 'url'>> {
  const filteredRecipes = recipes
    .filter(({categories}) => categories.includes(category))
    // Display large cards first.
    .sort((a, b) => (b.imageUrl ? 1 : 0) - (a.imageUrl ? 1 : 0));

  return filteredRecipes.map((recipe) => ({
    ...recipe,
    // Only show large cards if this is the first category in the list
    // This avoid showing 2 CTA for a single recipe
    imageUrl: recipe.categories[0] === category ? recipe.imageUrl : undefined,
    title: localize(recipe.title),
    description: localize(recipe.description),
    type: recipe.categories[0],
    ...getRecipeIconProps(recipe),
  }));
}
